import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { useTranslation } from "react-i18next";
import { Star, X, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export type ReviewSource = "popup" | "early" | "profile";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after successful submission */
  onSubmitted?: () => void;
  /** If true, show "never show again" option (for daily popup) */
  showNeverAgain?: boolean;
  onNeverAgain?: () => void;
  source?: ReviewSource;
}

type Lang = "en" | "es" | "de";

/** New survey answer shape, stored as jsonb in user_reviews.reasons */
type SurveyAnswers = {
  problems: string;
  expected: string;
  features: string;
  ai_issues: string;
};

const SURVEY_QUESTIONS: { key: keyof SurveyAnswers; en: string; es: string; de: string }[] = [
  {
    key: "problems",
    en: "Did you experience any problems using the app?",
    es: "¿Tuviste algún problema usando el app?",
    de: "Hattest du Probleme bei der Nutzung der App?",
  },
  {
    key: "expected",
    en: "What did you expect to find in the app that you didn't?",
    es: "¿Qué esperabas encontrar en el app y no encontraste?",
    de: "Was hast du im App erwartet, aber nicht gefunden?",
  },
  {
    key: "features",
    en: "What features do you consider important in an app like this?",
    es: "¿Qué funciones consideras importantes en un app como este?",
    de: "Welche Funktionen hältst du in einer App wie dieser für wichtig?",
  },
  {
    key: "ai_issues",
    en: "Was there any AI feature that didn't work as expected?",
    es: "¿Hubo alguna función de IA que no funcionó como esperabas?",
    de: "Gab es eine KI-Funktion, die nicht wie erwartet funktioniert hat?",
  },
];

// Legacy chip labels — only used to display OLD reviews (array-of-keys format)
// in the "already reviewed" summary. New submissions never write this shape.
const LEGACY_REASON_LABELS: Record<string, string> = {
  receitas_incompletas: "🍽 Incomplete recipes",
  pouco_conteudo: "📚 Not enough content",
  lingua_incorreta: "🌐 Wrong language",
  dificil_de_usar: "🧭 Hard to navigate",
  esperava_diferente: "💭 Expected something different",
  problema_tecnico: "⚙️ Technical problem",
  preco_alto: "💰 Price too high",
  outro: "✏️ Other",
};

const STAR_LABELS: Record<Lang, string[]> = {
  en: ["", "Very bad", "Bad", "Ok", "Good", "Excellent"],
  es: ["", "Muy malo", "Malo", "Regular", "Bueno", "Excelente"],
  de: ["", "Sehr schlecht", "Schlecht", "Okay", "Gut", "Ausgezeichnet"],
};

type Step = "stars" | "survey" | "done";

type ExistingReview = {
  id: string;
  stars: number;
  reasons: string[] | SurveyAnswers | null;
  comment: string | null;
};

const isSurveyAnswers = (r: ExistingReview["reasons"]): r is SurveyAnswers =>
  !!r && typeof r === "object" && !Array.isArray(r);

const emptyAnswers = (): SurveyAnswers => ({ problems: "", expected: "", features: "", ai_issues: "" });

export default function UserReviewModal({
  open, onClose, onSubmitted, showNeverAgain, onNeverAgain, source = "popup",
}: Props) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { language, market } = useLocale();
  const lang = language as Lang;

  const tr = (en: string, es: string, de: string) => (lang === "en" ? en : lang === "de" ? de : es);

  const [step, setStep] = useState<Step>("stars");
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>(emptyAnswers());
  const [saving, setSaving] = useState(false);

  // Existing review state
  const [existing, setExisting] = useState<ExistingReview | null | undefined>(undefined); // undefined = loading
  const [editing, setEditing] = useState(false);

  // Load existing review on open
  useEffect(() => {
    if (!open || !user?.id) return;

    let cancelled = false;

    // Safety timeout: if the query hangs for any reason, close the modal
    // instead of leaving the user trapped behind a blurry unresponsive overlay.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        onClose();
      }
    }, 5000);

    (async () => {
      try {
        const { data: appUser } = await supabase
          .from("app_users")
          .select("id")
          .eq("id", user.id)
          .maybeSingle();

        // If user not found in app_users, exit gracefully — do NOT leave
        // `existing` as undefined (which would trap the user in a blurry
        // fullscreen spinner with no way to close it).
        if (!appUser?.id) {
          if (!cancelled) { cancelled = true; clearTimeout(safetyTimer); onClose(); }
          return;
        }

        if (cancelled) return;

        const { data } = await supabase
          .from("user_reviews" as any)
          .select("id, stars, reasons, comment")
          .eq("user_id", appUser.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!cancelled) {
          clearTimeout(safetyTimer);
          setExisting((data as unknown as ExistingReview | null) ?? null);
        }
      } catch {
        // On any DB error, close the modal instead of trapping the user
        if (!cancelled) { cancelled = true; clearTimeout(safetyTimer); onClose(); }
      }
    })();

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, [open, user?.id, onClose]);

  const resetAndClose = () => {
    setStep("stars");
    setStars(0);
    setHovered(0);
    setAnswers(emptyAnswers());
    setEditing(false);
    setExisting(undefined);
    onClose();
  };

  const startEdit = () => {
    if (!existing) return;
    setStars(existing.stars);
    setAnswers(isSurveyAnswers(existing.reasons) ? existing.reasons : emptyAnswers());
    setEditing(true);
    setStep("survey");
  };

  const handleStarSelect = (n: number) => {
    setStars(n);
    setTimeout(() => setStep("survey"), 200);
  };

  const setAnswer = (key: keyof SurveyAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const allAnswered = SURVEY_QUESTIONS.every((q) => answers[q.key].trim().length > 0);

  const handleSubmit = async () => {
    if (!user?.id || stars === 0 || !allAnswered) return;
    setSaving(true);
    try {
      const { data: appUser } = await supabase
        .from("app_users")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      if (!appUser?.id) throw new Error("User not found");

      const payload = {
        stars,
        reasons: answers,
        comment: answers.problems.trim() || null,
      };

      if (editing && existing?.id) {
        // UPDATE existing review
        const { error } = await supabase
          .from("user_reviews" as any)
          .update(payload as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        // INSERT new review
        const { error } = await supabase.from("user_reviews" as any).insert({
          user_id: appUser.id,
          source,
          language: i18n.language,
          market,
          context: window.location.pathname,
          ...payload,
        } as any);
        if (error) throw error;
      }

      setStep("done");
      onSubmitted?.();
    } catch (err: any) {
      console.error("[UserReviewModal] Failed to save review:", err);
      toast({ title: tr("Something went wrong", "Algo salió mal", "Etwas ist schiefgelaufen"), description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // Loading existing review
  if (existing === undefined) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={resetAndClose} />
        <div className="relative w-full sm:max-w-sm bg-background rounded-2xl shadow-2xl overflow-hidden flex items-center justify-center py-12">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:bg-muted/40 z-10"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Already reviewed — show summary (unless editing)
  if (existing && !editing && step === "stars") {
    const legacyReasons = Array.isArray(existing.reasons) ? existing.reasons : [];
    const surveyReasons = isSurveyAnswers(existing.reasons) ? existing.reasons : null;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={resetAndClose} />
        <div className="relative w-full sm:max-w-sm bg-background rounded-2xl shadow-2xl overflow-hidden animate-slide-up max-h-[85vh] overflow-y-auto">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:bg-muted/40 z-10"
          >
            <X size={18} />
          </button>

          <div className="px-6 pt-8 pb-7 text-center space-y-4">
            <div className="text-4xl">✅</div>
            <div>
              <p className="text-xl font-extrabold text-foreground">
                {tr("You already reviewed!", "¡Ya has opinado!", "Du hast bereits bewertet!")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {tr("Here's what you said:", "Esto es lo que dijiste:", "Das hast du gesagt:")}
              </p>
            </div>

            <div className="flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={28} className={n <= existing.stars ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"} />
              ))}
            </div>

            {surveyReasons && (
              <div className="text-left space-y-3">
                {SURVEY_QUESTIONS.map((q) => (
                  surveyReasons[q.key] ? (
                    <div key={q.key}>
                      <p className="text-[11px] font-bold text-muted-foreground">{tr(q.en, q.es, q.de)}</p>
                      <p className="text-sm text-foreground bg-muted/20 rounded-lg p-2.5 mt-1 leading-relaxed">
                        {surveyReasons[q.key]}
                      </p>
                    </div>
                  ) : null
                ))}
              </div>
            )}

            {legacyReasons.length > 0 && (
              <div className="flex flex-wrap gap-1.5 justify-center">
                {legacyReasons.map((r) => (
                  <span key={r} className="px-2.5 py-1 rounded-lg bg-muted/40 text-xs font-semibold text-muted-foreground">
                    {LEGACY_REASON_LABELS[r] || r}
                  </span>
                ))}
              </div>
            )}

            {!surveyReasons && existing.comment && (
              <p className="text-sm text-muted-foreground italic">"{existing.comment}"</p>
            )}

            <button
              onClick={startEdit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-muted/60 text-foreground font-bold text-sm"
            >
              <Pencil size={14} />
              {tr("Edit my review", "Editar mi opinión", "Meine Bewertung bearbeiten")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={step === "done" ? resetAndClose : undefined} />
      <div className="relative w-full sm:max-w-sm bg-background rounded-2xl shadow-2xl overflow-hidden animate-slide-up max-h-[85vh] flex flex-col">

        {/* Close button */}
        {step !== "done" && (
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:bg-muted/40 z-10"
          >
            <X size={18} />
          </button>
        )}

        {/* ── Step: Stars ── */}
        {step === "stars" && (
          <div className="px-6 pt-8 pb-7 text-center space-y-5">
            <div>
              <p className="text-xl font-extrabold text-foreground">
                {tr("How's your experience?", "¿Cómo está siendo tu experiencia?", "Wie ist deine Erfahrung?")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {tr("Your feedback helps us improve", "Tu feedback nos ayuda a mejorar", "Dein Feedback hilft uns, besser zu werden")}
              </p>
            </div>

            <div className="flex justify-center gap-3 py-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => handleStarSelect(n)}
                  onMouseEnter={() => setHovered(n)}
                  onMouseLeave={() => setHovered(0)}
                  className="transition-transform active:scale-90 hover:scale-110"
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      n <= (hovered || stars)
                        ? "text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]"
                        : "text-muted-foreground/30"
                    }`}
                  />
                </button>
              ))}
            </div>

            {(hovered > 0 || stars > 0) && (
              <p className="text-sm font-semibold text-foreground -mt-1 animate-fade-in">
                {STAR_LABELS[lang][hovered || stars]}
              </p>
            )}

            {showNeverAgain && (
              <button onClick={onNeverAgain} className="text-[11px] text-muted-foreground hover:text-foreground underline">
                {tr("Don't ask me again", "No preguntarme de nuevo", "Nicht mehr fragen")}
              </button>
            )}
          </div>
        )}

        {/* ── Step: Survey (single scroll, 4 open questions) ── */}
        {step === "survey" && (
          <div className="flex flex-col min-h-0">
            <div className="px-6 pt-7 pb-2 shrink-0">
              <div className="flex gap-0.5 mb-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={14} className={n <= stars ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"} />
                ))}
              </div>
              <p className="text-lg font-extrabold text-foreground">
                {tr("A few quick questions", "Algunas preguntas rápidas", "Ein paar kurze Fragen")}
              </p>
            </div>

            <div className="px-6 py-3 space-y-4 overflow-y-auto">
              {SURVEY_QUESTIONS.map((q) => (
                <div key={q.key}>
                  <p className="text-sm font-semibold text-foreground mb-1.5">{tr(q.en, q.es, q.de)}</p>
                  <textarea
                    value={answers[q.key]}
                    onChange={(e) => setAnswer(q.key, e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder={tr("Your answer...", "Tu respuesta...", "Deine Antwort...")}
                    className="w-full p-3 rounded-xl border border-border bg-muted/20 text-foreground text-sm placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              ))}
            </div>

            <div className="px-6 pt-3 pb-6 space-y-2.5 shrink-0 border-t border-border/40">
              <button
                onClick={handleSubmit}
                disabled={saving || !allAnswered}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-40 transition-all active:scale-[0.97]"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  editing
                    ? tr("Save changes", "Guardar cambios", "Änderungen speichern")
                    : tr("Submit", "Enviar", "Absenden")
                )}
              </button>
              <button
                onClick={resetAndClose}
                className="w-full py-2 text-sm font-medium text-center text-muted-foreground hover:text-foreground transition-colors"
              >
                {tr("Leave for later", "Dejar para después", "Später")}
              </button>
            </div>
          </div>
        )}

        {/* ── Step: Done ── */}
        {step === "done" && (
          <div className="px-6 pt-10 pb-8 text-center space-y-4">
            <div className="text-5xl mb-2">
              {stars >= 4 ? "🎉" : "🙏"}
            </div>
            <div>
              <p className="text-xl font-extrabold text-foreground">
                {stars >= 4
                  ? tr("Thanks so much!", "¡Muchas gracias!", "Vielen Dank!")
                  : tr("Thanks for the honesty!", "¡Gracias por la sinceridad!", "Danke für deine Ehrlichkeit!")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {stars >= 4
                  ? tr("Your support means a lot to us.", "Tu apoyo significa mucho para nosotros.", "Deine Unterstützung bedeutet uns viel.")
                  : tr("We're working to get better.", "Estamos trabajando para mejorar.", "Wir arbeiten daran, besser zu werden.")}
              </p>
            </div>
            <button
              onClick={resetAndClose}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
            >
              <CheckCircle2 size={15} /> {tr("Done", "Listo", "Fertig")}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
